/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import config from 'garaje/config/environment';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';

interface ReactAppComponentArgs {
  name: string;
  injectClass?: string;
}

export default class ReactAppComponent extends Component<ReactAppComponentArgs> {
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare abilities: AbilitiesService;
  @service declare metrics: MetricsService;

  constructor(owner: unknown, args: ReactAppComponentArgs) {
    super(owner, args);

    const { cookieDomain: domain } = config;

    document.cookie = `location_id=${this.state.currentLocation.id};domain=${domain};path=/;samesite=lax`;

    void this.inject();
  }

  get hasScreensPlan(): boolean {
    return !!this.state.features?.hasScreensDevices;
  }

  get hasPaidScreensPlan(): boolean {
    return !!this.state.screensSubscription?.subscribed && !this.state.screensSubscription?.isOnTrial;
  }

  get hasAnyEnvoyPaidPlan(): boolean {
    const hasWorkplace = !!this.state.workplaceSubscription?.subscribed && !this.state.workplaceSubscription?.isOnTrial;
    const hasVisitors = !!this.state.visitorsSubscription?.subscribed && !this.state.visitorsSubscription?.isOnTrial;
    const hasRooms = !!this.state.roomsSubscription?.subscribed && !this.state.roomsSubscription?.isOnTrial;
    const hasDesks = !!this.state.desksSubscription?.subscribed && !this.state.desksSubscription?.isOnTrial;
    const hasDeliveries =
      !!this.state.deliveriesSubscription?.subscribed && !this.state.deliveriesSubscription?.isOnTrial;
    const hasRealRooms = !!this.state.realRoomsSubscription?.subscribed && !this.state.realRoomsSubscription?.isOnTrial;
    const hasRealDesks = !!this.state.realDesksSubscription?.subscribed && !this.state.realDesksSubscription?.isOnTrial;
    const hasRealDeliveries =
      !!this.state.realDeliveriesSubscription?.subscribed && !this.state.realDeliveriesSubscription?.isOnTrial;
    const hasVfd = !!this.state.vfdSubscription?.subscribed && !this.state.vfdSubscription?.isOnTrial;
    const hasEmno = !!this.state.emnoSubscription?.subscribed && !this.state.emnoSubscription?.isOnTrial;

    return (
      hasWorkplace ||
      hasVisitors ||
      hasRooms ||
      hasDesks ||
      hasDeliveries ||
      hasRealRooms ||
      hasRealDesks ||
      hasRealDeliveries ||
      hasVfd ||
      hasEmno
    );
  }

  hasPlan(plan: string): boolean {
    if (plan === 'screens') {
      return this.hasScreensPlan;
    }
    if (plan === 'screens-paid') {
      return this.hasPaidScreensPlan;
    }
    if (plan === 'envoy-paid') {
      return this.hasAnyEnvoyPaidPlan;
    }
    return false;
  }

  @action
  async inject(): Promise<void> {
    const { cookieDomain } = config;
    const domain = cookieDomain.includes('christmas') ? `apps.${cookieDomain}` : cookieDomain;

    const url = `https://sapi.${domain}/s/${this.args.name}`;

    await import(/* webpackIgnore: true */ url);

    const container = document.getElementById('inject')!;
    container.appendChild(document.createElement(`x-envoy-${this.args.name}`));

    window.addEventListener('message', this.onWindowMessage, false);
  }

  onWindowMessage: (e: MessageEvent) => void = (e: MessageEvent<{ event: string; payload?: any }>) => {
    const event = e?.data?.event;
    const payload = e?.data?.payload;

    if (event === 'x:app:navigate') {
      this.router.transitionTo(payload?.href);
    }

    if (event === 'x:app:track') {
      this.metrics.trackEvent(payload?.event, payload?.properties);
    }

    if (event === 'x:app:toast') {
      this.flashMessages.showAndHideFlash(payload?.type, payload?.message);
    }

    if (event === 'x:app:feature-enabled') {
      window.postMessage({
        event: 'x:app:feature-enabled:response',
        payload: { enabled: this.featureFlags.isEnabled(payload.feature) },
      });
    }

    if (event === 'x:app:can') {
      window.postMessage({
        event: 'x:app:can:response',
        payload: { ability: payload.ability, allowed: this.abilities.can(payload.ability) },
      });
    }

    if (event === 'x:app:plan') {
      window.postMessage({
        event: 'x:app:plan:response',
        payload: { plan: payload.plan, hasPlan: this.hasPlan(payload.plan) },
      });
    }

    if (event === 'x:app:get-location') {
      const location = this.state.currentLocation;

      window.postMessage({
        event: 'x:app:get-location:response',
        payload: {
          location: {
            id: location.id,
            name: location.name,
            timezone: location.timezone,
          },
        },
      });
    }
  };

  willDestroy(): void {
    super.willDestroy();
    window.removeEventListener('message', this.onWindowMessage, false);
  }
}
